import * as amplitude from '@amplitude/analytics-browser'
import Cookie from 'cookie-universal'
import type { AnalyticsEvent } from '~/types/analyticsEvents'
import type { UserProfile } from '~/types/user'

const cookies = Cookie()

let didInitAmplitude = false
let amplitudeAPIKey: string

const identifyEvent = new amplitude.Identify()

export const useAmplitudeTrackingService = () => {
  function setUser(user: UserProfile) {
    if (!amplitude || !didInitAmplitude || !user.t_id) return

    amplitude.setUserId(user.t_id.toString())

    identifyEvent.set('user_type', user.account_type)
    identifyEvent.set('number_follower', user.followers_count)
    identifyEvent.set('number_following', user.follows_count)
    identifyEvent.set('number_trips', user.trips_count)
    identifyEvent.set('auto_follow_enabled', user.auto_accept_follow_requests ?? false)

    amplitude.identify(identifyEvent)
  }

  function unsetUser() {
    if (!amplitude || !didInitAmplitude) return

    identifyEvent.clearAll()

    amplitude.setUserId(undefined)
  }

  function setUserProperties(properties: Record<string, string | string[] | number | boolean>) {
    if (!amplitude || !didInitAmplitude) return

    const identify = new amplitude.Identify()

    for (const [key, value] of Object.entries(properties)) {
      identify.set(key, value)
    }

    amplitude.identify(identify)
  }

  function setApiKey(key: string) {
    amplitudeAPIKey = key
  }

  function optOut() {
    amplitude.setOptOut(true)
    // Remove amplitude related cookies
    cookies.remove(`AMP_${amplitudeAPIKey.substring(0, 10)}`)
    cookies.remove(`AMP_MKTG${amplitudeAPIKey.substring(0, 10)}`)
    cookies.remove('AMP_TEST')
    cookies.remove('AMP_TDLTEST')
  }

  function init() {
    if (!amplitudeAPIKey) {
      console.warn('Amplitude api key is not set')
      return
    }
    if (!import.meta.env.SSR) {
      amplitude.init(amplitudeAPIKey, { defaultTracking: false })

      didInitAmplitude = true
    }
  }

  function trackEvent(args: AnalyticsEvent) {
    if (!amplitudeAPIKey) {
      console.warn('Amplitude api key is not set')

      return false
    }

    if (amplitude && didInitAmplitude) {
      if (!amplitude.getUserId()) {
        console.warn('Amplitude event tracked without user id')
      }

      amplitude.track(...args)
    }

    return true
  }

  return {
    setUser,
    unsetUser,
    setUserProperties,
    setApiKey,
    optOut,
    init,
    trackEvent
  }
}
